import React, { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Form,
  InputGroup,
  Modal,
  Toast,
  ToastContainer,
  Spinner,
  Pagination,
  Alert,
} from 'react-bootstrap';
import {
  FileText,
  CheckCircle,
  XCircle,
  Search,
  Cash,
  People,
  InfoCircle,
  Check,
  Clock,
  ChevronDown,
  ChevronUp,
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/AuthContext';

// Données fictives
const mockRequests = [
  { id: 'R001', date: '2025-03-12', patientName: 'Jean Dupont', patientId: 'CNAMGS-123456', pharmacy: 'Pharmacie Centrale', amount: 15000, status: 'pending' },
  { id: 'R002', date: '2025-03-11', patientName: 'Marie Nzogho', patientId: 'CNAMGS-789012', pharmacy: 'Pharmacie Moderne', amount: 8000, status: 'pending' },
  { id: 'R003', date: '2025-03-10', patientName: 'Pierre Mba', patientId: 'CNAMGS-345678', pharmacy: 'Pharmacie du Quartier', amount: 12000, status: 'pending' },
  { id: 'R004', date: '2025-03-09', patientName: 'Sophie Nguema', patientId: 'CNAMGS-111222', pharmacy: 'Pharmacie Centrale', amount: 5000, status: 'approved' },
  { id: 'R005', date: '2025-03-08', patientName: 'Paul Obiang', patientId: 'CNAMGS-333444', pharmacy: 'Pharmacie Moderne', amount: 9500, status: 'rejected' },
  { id: 'R006', date: '2025-03-07', patientName: 'Claire Moussavou', patientId: 'CNAMGS-555666', pharmacy: 'Pharmacie du Quartier', amount: 22000, status: 'pending' },
  { id: 'R007', date: '2025-03-06', patientName: 'David Ngoma', patientId: 'CNAMGS-777888', pharmacy: 'Pharmacie Centrale', amount: 7500, status: 'approved' },
  { id: 'R008', date: '2025-03-05', patientName: 'Anne Mintogo', patientId: 'CNAMGS-999000', pharmacy: 'Pharmacie Moderne', amount: 18000, status: 'pending' },
];

const ReimbursementValidation = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState(mockRequests);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedRows(newSet);
  };

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  const filteredRequests = useMemo(() => {
    let filtered = requests;
    if (statusFilter !== 'all') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        r.id.toLowerCase().includes(term) ||
        r.patientName.toLowerCase().includes(term) ||
        r.patientId.toLowerCase().includes(term) ||
        r.pharmacy.toLowerCase().includes(term)
      );
    }
    return filtered;
  }, [requests, searchTerm, statusFilter]);

  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const paginatedRequests = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRequests.slice(start, start + itemsPerPage);
  }, [filteredRequests, currentPage, itemsPerPage]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge bg="warning" className="rounded-pill px-3 py-2"><Clock size={12} className="me-1" /> En attente</Badge>,
      approved: <Badge bg="success" className="rounded-pill px-3 py-2"><CheckCircle size={12} className="me-1" /> Approuvé</Badge>,
      rejected: <Badge bg="danger" className="rounded-pill px-3 py-2"><XCircle size={12} className="me-1" /> Rejeté</Badge>,
    };
    return badges[status] || <Badge bg="secondary">{status}</Badge>;
  };

  const stats = {
    total: filteredRequests.length,
    pending: filteredRequests.filter(r => r.status === 'pending').length,
    approved: filteredRequests.filter(r => r.status === 'approved').length,
    rejected: filteredRequests.filter(r => r.status === 'rejected').length,
    totalAmount: filteredRequests.reduce((sum, r) => sum + r.amount, 0),
  };

  const statsCards = [
    { title: 'Total demandes', value: stats.total, icon: FileText, color: '#4361ee', gradient: 'linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)', bg: 'white' },
    { title: 'En attente', value: stats.pending, icon: Clock, color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', bg: 'white' },
    { title: 'Approuvés', value: stats.approved, icon: CheckCircle, color: '#10b981', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', bg: 'white' },
    { title: 'Montant total', value: formatCurrency(stats.totalAmount), icon: Cash, color: '#06b6d4', gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', bg: 'white' },
  ];

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setShowApproveModal(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setShowRejectModal(true);
  };

  const confirmApprove = () => {
    setLoading(true);
    setTimeout(() => {
      setRequests(prev => prev.map(r => r.id === selectedRequest.id ? { ...r, status: 'approved' } : r));
      showToast(`Demande ${selectedRequest.id} approuvée`, 'success');
      setShowApproveModal(false);
      setSelectedRequest(null);
      setLoading(false);
    }, 800);
  };

  const confirmReject = () => {
    setLoading(true);
    setTimeout(() => {
      setRequests(prev => prev.map(r => r.id === selectedRequest.id ? { ...r, status: 'rejected' } : r));
      showToast(`Demande ${selectedRequest.id} rejetée`, 'danger');
      setShowRejectModal(false);
      setSelectedRequest(null);
      setLoading(false);
    }, 800);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const items = [];
    for (let i = 1; i <= totalPages; i++) {
      items.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
    }
    return (
      <div className="d-flex justify-content-end mt-3">
        <Pagination size="sm">
          <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
          {items}
          <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
        </Pagination>
      </div>
    );
  };

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={toast.show} onClose={() => setToast(prev => ({ ...prev, show: false }))} bg={toast.variant} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            <strong className="me-auto">
              {toast.variant === 'success' ? 'Succès' : toast.variant === 'danger' ? 'Erreur' : 'Information'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="container-fluid">
        {/* En-tête */}
        <div className="mb-4">
          <h4 className="main-title mb-0 d-flex align-items-center gap-2">
            <div className="bg-primary bg-opacity-10 rounded-circle p-2">
              <FileText size={24} className="text-primary" />
            </div>
            Validation des remboursements
          </h4>
          <p className="text-muted mb-0 mt-1">Approuvez ou rejetez les demandes de remboursement des pharmacies</p>
        </div>

        {/* Cartes statistiques */}
        <Row className="g-3 mb-4">
          {statsCards.map((card, idx) => (
            <Col md={6} lg={3} sm={6} xs={6} key={idx}>
              <Card className="border-0 shadow-sm stats-card h-100" style={{ borderRadius: '16px', background: card.bg }}>
                <div className="card-gradient" style={{ background: card.gradient, height: '4px' }}></div>
                <Card.Body className="p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="text-muted mb-1 small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px' }}>{card.title}</h6>
                      <h3 className="mb-0 fw-bold" style={{ fontSize: '1.6rem' }}>{card.value}</h3>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Filtres */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4 bg-white">
            <Row className="align-items-center g-3">
              <Col md={5}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-0"><Search size={18} className="text-muted" /></InputGroup.Text>
                  <Form.Control type="text" placeholder="Rechercher par patient, pharmacie ou numéro..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="border-0 bg-light" />
                </InputGroup>
              </Col>
              <Col md={3} sm={6} xs={6}>
                <Form.Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="border-0 bg-light">
                  <option value="all">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="approved">Approuvés</option>
                  <option value="rejected">Rejetés</option>
                </Form.Select>
              </Col>
              <Col md={2} sm={6} xs={6}>
                <Form.Select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="border-0 bg-light">
                  <option value={5}>5 par page</option>
                  <option value={10}>10 par page</option>
                  <option value={20}>20 par page</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <div className="text-muted small text-md-end">{filteredRequests.length} demande(s)</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tableau des demandes */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
          <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
            <h5 className="fw-semibold mb-0">Demandes de remboursement</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="bg-light">
                  <tr>
                    <th>Date</th>
                    <th>Patient</th>
                    <th className="d-none d-sm-table-cell">Pharmacie</th>
                    <th className="d-none d-sm-table-cell">Montant</th>
                    <th className="d-none d-sm-table-cell">Statut</th>
                    <th className="d-none d-sm-table-cell">Actions</th>
                    <th className="d-sm-none">Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRequests.length > 0 ? (
                    paginatedRequests.map(r => {
                      const isExpanded = expandedRows.has(r.id);
                      return (
                        <React.Fragment key={r.id}>
                          {/* Ligne principale : visible toujours, mais les colonnes supplémentaires sont masquées sur mobile */}
                          <tr>
                            <td data-label="Date" className="small">{formatDate(r.date)}</td>
                            <td data-label="Patient" className="fw-medium">
                              {r.patientName}<br /><small className="text-muted">{r.patientId}</small>
                            </td>
                            <td data-label="Pharmacie" className="d-none d-sm-table-cell">{r.pharmacy}</td>
                            <td data-label="Montant" className="fw-bold text-success d-none d-sm-table-cell">{formatCurrency(r.amount)}</td>
                            <td data-label="Statut" className="d-none d-sm-table-cell">{getStatusBadge(r.status)}</td>
                            <td data-label="Actions" className="d-none d-sm-table-cell">
                              {r.status === 'pending' ? (
                                <div className="d-flex gap-2 flex-wrap">
                                  <Button size="sm" variant="success" className="rounded-pill px-3" onClick={() => handleApprove(r)}>
                                    <Check size={14} className="me-1" /> Approuver
                                  </Button>
                                  <Button size="sm" variant="danger" className="rounded-pill px-3" onClick={() => handleReject(r)}>
                                    <XCircle size={14} className="me-1" /> Rejeter
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-muted">Aucune action</span>
                              )}
                            </td>
                            <td className="d-sm-none text-center" style={{ verticalAlign: 'middle' }}>
                              <Button variant="link" onClick={() => toggleRow(r.id)} className="p-0 text-secondary" style={{ textDecoration: 'none' }}>
                                {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                              </Button>
                            </td>
                          </tr>
                          {/* Ligne de détails : affichée seulement sur mobile et si dépliée */}
                          {isExpanded && (
                            <tr className="d-sm-none detail-row">
                              <td colSpan="7" className="p-3 bg-light">
                                <div className="row g-2">
                                  <div className="col-6">
                                    <strong>Pharmacie :</strong> {r.pharmacy}
                                  </div>
                                  <div className="col-6">
                                    <strong>Montant :</strong> {formatCurrency(r.amount)}
                                  </div>
                                  <div className="col-6">
                                    <strong>Statut :</strong> {getStatusBadge(r.status)}
                                  </div>
                                  <div className="col-12">
                                    <strong>Actions :</strong>
                                    {r.status === 'pending' ? (
                                      <div className="d-flex gap-2 mt-1">
                                        <Button size="sm" variant="success" className="rounded-pill" onClick={() => handleApprove(r)}>
                                          <Check size={14} className="me-1" /> Approuver
                                        </Button>
                                        <Button size="sm" variant="danger" className="rounded-pill" onClick={() => handleReject(r)}>
                                          <XCircle size={14} className="me-1" /> Rejeter
                                        </Button>
                                      </div>
                                    ) : (
                                      <span className="text-muted ms-2">Aucune action</span>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-5">
                        <FileText size={48} className="text-muted mb-3" />
                        <p className="text-muted">Aucune demande trouvée</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            {renderPagination()}
          </Card.Body>
        </Card>
        <br></br> <br></br>

        {/* Modals (inchangés) */}
        <Modal show={showApproveModal} onHide={() => setShowApproveModal(false)} centered>
          <Modal.Header closeButton className="bg-success text-white">
            <Modal.Title className="d-flex align-items-center gap-2"><CheckCircle size={20} /> Approuver le remboursement</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedRequest && (
              <>
                <div className="mb-3 p-3 rounded-3" style={{ background: '#e8f5e9', borderLeft: '4px solid #10b981' }}>
                  <div className="d-flex align-items-center gap-2">
                    <People size={18} className="text-success" />
                    <strong>Patient :</strong> {selectedRequest.patientName} <span className="text-muted">({selectedRequest.patientId})</span>
                  </div>
                </div>
                <Row>
                  <Col md={6}><div className="mb-3"><small className="text-muted d-block">Pharmacie</small><strong>{selectedRequest.pharmacy}</strong></div>
                  <div className="mb-3"><small className="text-muted d-block">Date</small><strong>{formatDate(selectedRequest.date)}</strong></div></Col>
                  <Col md={6}><div className="mb-3"><small className="text-muted d-block">Montant</small><strong className="text-success">{formatCurrency(selectedRequest.amount)}</strong></div></Col>
                </Row>
                <Alert variant="info" className="mt-2"><InfoCircle size={14} className="me-1" /> Après approbation, le remboursement sera traité dans les prochains jours.</Alert>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowApproveModal(false)} className="rounded-pill">Annuler</Button>
            <Button variant="success" onClick={confirmApprove} disabled={loading} className="rounded-pill px-4">
              {loading ? <Spinner size="sm" className="me-1" /> : <Check size={16} className="me-1" />}
              {loading ? 'Traitement...' : 'Confirmer'}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
          <Modal.Header closeButton className="bg-danger text-white">
            <Modal.Title className="d-flex align-items-center gap-2"><XCircle size={20} /> Rejeter le remboursement</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedRequest && (
              <>
                <div className="mb-3 p-3 rounded-3" style={{ background: '#fee2e2', borderLeft: '4px solid #ef4444' }}>
                  <div className="d-flex align-items-center gap-2">
                    <People size={18} className="text-danger" />
                    <strong>Patient :</strong> {selectedRequest.patientName} <span className="text-muted">({selectedRequest.patientId})</span>
                  </div>
                </div>
                <Row>
                  <Col md={6}><div className="mb-3"><small className="text-muted d-block">Pharmacie</small><strong>{selectedRequest.pharmacy}</strong></div>
                  <div className="mb-3"><small className="text-muted d-block">Date</small><strong>{formatDate(selectedRequest.date)}</strong></div></Col>
                  <Col md={6}><div className="mb-3"><small className="text-muted d-block">Montant</small><strong className="text-danger">{formatCurrency(selectedRequest.amount)}</strong></div></Col>
                </Row>
                <Alert variant="danger" className="mt-2"><InfoCircle size={14} className="me-1" /> Une notification sera envoyée à la pharmacie pour justifier le rejet.</Alert>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRejectModal(false)} className="rounded-pill">Annuler</Button>
            <Button variant="danger" onClick={confirmReject} disabled={loading} className="rounded-pill px-4">
              {loading ? <Spinner size="sm" className="me-1" /> : <XCircle size={16} className="me-1" />}
              {loading ? 'Traitement...' : 'Rejeter'}
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .card-gradient {
            transition: height 0.2s ease;
          }
          .stats-card:hover .card-gradient {
            height: 6px;
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
            vertical-align: middle;
          }
          @media (max-width: 767.98px) {
            .table td, .table th {
              padding: 0.5rem;
            }
            .btn-sm {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ReimbursementValidation;